/* eslint-disable import/prefer-default-export */
const announcerTimers = new Map();

const TOAST_REGION_ID = 'a11y-toast-announcement-region';
const TOAST_ALERT_REGION_ID = 'a11y-toast-alert-region';
// Production uses antd's default `ant-` prefix (the `and-` in the vendor audit's HTML is a
// transcription artifact), so exact class selectors would also work; the substring forms are kept
// because they are verified against antd's message DOM. `notification` notices are matched on their
// root class only: every child class also starts with `ant-notification-notice-`, and a substring
// match would treat each child as a new notice.
const TOAST_NOTICE_SELECTOR =
  '[class*="message-notice"], [class$="-notification-notice"], [class*="-notification-notice "]';
// antd puts `ant-message-<type>` on a message's content and `ant-notification-notice-icon-<type>`
// on a notification's icon, so errors are told apart by those.
const TOAST_ERROR_SELECTOR = '[class*="message-error"], [class*="notification-notice-icon-error"]';
const NOTIFICATION_TEXT_SELECTOR =
  '[class$="-notification-notice-message"], [class$="-notification-notice-description"]';

// @ant-design/icons names every glyph from its own filename, so a purely decorative icon is read
// as e.g. "exclamation-circle". It applies `role`/`aria-label` before spreading caller props, so
// overriding both with undefined is what actually drops them from the DOM.
export const DECORATIVE_ICON_PROPS = {
  'aria-hidden': 'true',
  role: undefined,
  'aria-label': undefined,
};

// antd's Radio.Group renders its own <div class="ant-radio-group"> but forwards only className,
// style, id and the mouse handlers, so role/aria-* handed to it as props never reach the DOM.
// The rendered group has to be found and annotated directly. Inside a legacy Form the id is the
// field name: @ant-design/compatible configures rc-form with `fieldNameProp: 'id'`.
export function applyRadioGroupSemantics(group, { label, invalid, describedBy } = {}) {
  if (!group) {
    return;
  }
  group.setAttribute('role', 'radiogroup');
  if (label) {
    group.setAttribute('aria-label', label);
  }
  if (invalid !== undefined) {
    group.setAttribute('aria-invalid', String(!!invalid));
  }
  if (invalid && describedBy) {
    group.setAttribute('aria-describedby', describedBy);
  } else {
    group.removeAttribute('aria-describedby');
  }
}

// rc-tabs puts role="tablist" on the outer bar but renders the tabs five wrappers below it, so the
// tabs are never the tablist's accessible children, and the scroll arrows sit inside it as well.
// Marking the wrappers presentational collapses them out of the accessibility tree, which leaves
// the tablist owning the tabs and nothing else. Nothing visual or focusable changes.
export function applyTabListSemantics(tablist) {
  if (!tablist) {
    return;
  }
  tablist.querySelectorAll('[role="tab"]').forEach((tab) => {
    let wrapper = tab.parentElement;
    while (wrapper && wrapper !== tablist) {
      wrapper.setAttribute('role', 'presentation');
      wrapper = wrapper.parentElement;
    }
  });
  // Decorative scroll affordances; their antd icons otherwise read as "left" / "right". The ink bar is
  // the underline under the active tab. A tablist may only own tabs, and aria-hidden alone does not
  // take these out of its children, so they are also made presentational.
  tablist
    .querySelectorAll('[class*="tabs-tab-prev"], [class*="tabs-tab-next"], [class*="tabs-ink-bar"]')
    .forEach((decoration) => {
      decoration.setAttribute('role', 'presentation');
      decoration.setAttribute('aria-hidden', 'true');
    });
  // rc-tabs 10 points each tab's aria-controls at "tabpane-<key>" but renders the panels without ids.
  // Panels follow the tabs' order, so they are paired by position.
  const tabs = tablist.querySelectorAll('[role="tab"]');
  const panels = tablist.parentElement.querySelectorAll(':scope > [class*="tabs-content"] > [role="tabpanel"]');
  if (tabs.length !== panels.length) {
    return;
  }
  tabs.forEach((tab, index) => {
    const panelId = tab.getAttribute('aria-controls');
    if (panelId && tab.id) {
      panels[index].id = panelId;
      panels[index].setAttribute('aria-labelledby', tab.id);
    }
  });
}

const SHARE_BUTTON_SELECTOR = '[class*="react-share__ShareButton"]';

function fixShareButtons(node) {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }
  const buttons = node.matches(SHARE_BUTTON_SELECTOR) ? [node] : [...node.querySelectorAll(SHARE_BUTTON_SELECTOR)];
  buttons.forEach((button) => {
    const visibleLabel = button.textContent.trim();
    button.setAttribute('role', 'link');
    if (visibleLabel) {
      button.setAttribute('aria-label', `Share on ${visibleLabel}`);
    }
  });
}

// rc-select 10 renders a multiple-mode combobox input without aria-expanded, and an aria-expanded
// passed to <Select> lands on its wrapper div, where the attribute is not allowed.
export function setComboboxExpanded(inputId, expanded) {
  const input = document.getElementById(inputId);
  if (input) {
    input.setAttribute('aria-expanded', String(expanded));
  }
}

// rc-select 10 keeps the combobox input's value empty, and NVDA does not read aria-describedby when
// arrowing onto a field in browse mode, so the selection goes in the accessible name: the owned
// element holds "<label>, <selection>" and names the input. The label is the field's own <label for>.
export function nameComboboxWithSelection(inputId, nameId, selectionText) {
  const input = document.getElementById(inputId);
  const name = document.getElementById(nameId);
  if (!input || !name) {
    return;
  }
  const label = document.querySelector(`label[for="${CSS.escape(inputId)}"]`);
  const labelText = label ? label.textContent.trim() : '';
  name.textContent = labelText ? `${labelText}, ${selectionText}` : selectionText;
  input.setAttribute('aria-labelledby', nameId);
}

let toastAnnouncerInstalled = false;
const announcedNotices = new WeakSet();

function announceToRegion(region, message) {
  if (!region) {
    return;
  }
  const key = region.id || region;
  clearTimeout(announcerTimers.get(key));
  region.textContent = '';
  const timer = setTimeout(() => {
    region.textContent = message;
  }, 100);
  announcerTimers.set(key, timer);
}

export function announceToScreenReader(message, id = 'a11y-announcement-region') {
  announceToRegion(document.getElementById(id), message);
}

const MODAL_REGION_CLASS = 'a11y-modal-announcement-region';
const MODAL_ALERT_REGION_CLASS = 'a11y-modal-alert-region';
const MODAL_SELECTOR = '[role="dialog"][aria-modal="true"]';

function createRegion(role, live) {
  const region = document.createElement('div');
  region.className = 'arc-sr-only';
  region.setAttribute('role', role);
  region.setAttribute('aria-live', live);
  return region;
}

function ensureModalRegion(dialog) {
  let region = dialog.querySelector(`.${MODAL_REGION_CLASS}`);
  if (!region) {
    region = createRegion('status', 'polite');
    region.classList.add(MODAL_REGION_CLASS);
    dialog.appendChild(region);
  }
  return region;
}

function ensureModalAlertRegion(dialog) {
  let region = dialog.querySelector(`.${MODAL_ALERT_REGION_CLASS}`);
  if (!region) {
    region = createRegion('alert', 'assertive');
    region.classList.add(MODAL_ALERT_REGION_CLASS);
    dialog.appendChild(region);
  }
  return region;
}

// Both kinds are installed together, not lazily when a toast of that kind arrives: a live region
// created in the same tick as its text is not reliably announced.
function ensureDialogRegions(dialog) {
  ensureModalRegion(dialog);
  ensureModalAlertRegion(dialog);
}

function installModalRegions(node) {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }
  if (node.matches(MODAL_SELECTOR)) {
    ensureDialogRegions(node);
  }
  node.querySelectorAll(MODAL_SELECTOR).forEach(ensureDialogRegions);
}

function getVisibleDialogs() {
  return [...document.querySelectorAll(MODAL_SELECTOR)].filter(
    (dialog) => dialog.offsetParent !== null || dialog.getClientRects().length > 0,
  );
}

// Dialogs stack (cart -> discount) and both carry aria-modal, so which subtree a screen reader is
// willing to read is not something we can determine from here - VoiceOver and NVDA disagree about
// nested modals. The message therefore goes to every open dialog's region: assistive tech exposes
// only one of those subtrees, so exactly one copy is ever spoken, and the others sit in subtrees it
// is ignoring. The body-level region is used only when no modal is open.
function getAnnouncementRegions() {
  const dialogs = getVisibleDialogs();
  if (dialogs.length) {
    return dialogs.map(ensureModalRegion);
  }
  return [document.getElementById(TOAST_REGION_ID)];
}

// Errors go to an assertive region so they survive a polite message that is already being spoken:
// the discount flow puts "Checking discount code" in a polite region ~300ms before the failure
// arrives, and as a polite update the error loses that race - VoiceOver has no real polite queue,
// a later update supersedes an unspoken one.
//
// Unlike getAnnouncementRegions this deliberately returns ONE region rather than broadcasting.
// An assertive update is read even when the cursor sits outside its subtree, so the "exactly one
// copy is spoken" reasoning above does not hold here and a broadcast would be heard twice. The
// topmost dialog is the last one in the DOM - antd appends each modal container to <body> in open
// order.
function getAlertRegion() {
  const dialogs = getVisibleDialogs();
  if (dialogs.length) {
    return ensureModalAlertRegion(dialogs[dialogs.length - 1]);
  }
  return document.getElementById(TOAST_ALERT_REGION_ID);
}

function announceToast(node) {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }
  const notice = node.matches(TOAST_NOTICE_SELECTOR) ? node : node.querySelector(TOAST_NOTICE_SELECTOR);
  if (!notice || announcedNotices.has(notice)) {
    return;
  }
  announcedNotices.add(notice);
  // A notification's title and description are sibling divs; joined raw they would run together.
  const notificationParts = [...notice.querySelectorAll(NOTIFICATION_TEXT_SELECTOR)]
    .map((part) => part.textContent.trim())
    .filter(Boolean)
    .map((part) => (/[.!?]$/.test(part) ? part : `${part}.`));
  const text = notificationParts.length ? notificationParts.join(' ') : notice.textContent.trim();
  if (!text) {
    return;
  }
  if (notice.matches(TOAST_ERROR_SELECTOR) || notice.querySelector(TOAST_ERROR_SELECTOR)) {
    announceToRegion(getAlertRegion(), text);
    return;
  }
  getAnnouncementRegions().forEach((region) => announceToRegion(region, text));
}

function ensureBodyRegion(id, role, live) {
  if (!document.getElementById(id)) {
    const region = createRegion(role, live);
    region.id = id;
    document.body.appendChild(region);
  }
}

function startToastAnnouncer() {
  ensureBodyRegion(TOAST_REGION_ID, 'status', 'polite');
  ensureBodyRegion(TOAST_ALERT_REGION_ID, 'alert', 'assertive');

  // Antd's toasts are announced by mirroring their text into the regions above, not by rendering
  // them inside one: message.config() only reaches the antd instance the caller imported, and
  // prebuilt dependencies (arcl) resolve to a second copy whose toasts would stay silent.
  installModalRegions(document.body);
  fixShareButtons(document.body);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        installModalRegions(node);
        fixShareButtons(node);
        announceToast(node);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function installToastAnnouncer() {
  if (toastAnnouncerInstalled) {
    return;
  }
  toastAnnouncerInstalled = true;

  // The regions have to exist before a toast fires - a live region created in the same tick as its
  // text is not reliably announced - and this runs at bundle init, before <body> may exist.
  if (document.body) {
    startToastAnnouncer();
  } else {
    document.addEventListener('DOMContentLoaded', startToastAnnouncer, { once: true });
  }
}

const MODAL_FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// antd's Modal adds two focus-trap sentinels (aria-hidden, 0x0, tabindex=0 divs) at the start and end of
// `.ant-modal`; keyboard focus landing on them is an invisible, unnamed stop (WCAG 2.4.3). Take them out of
// the tab order and wrap Tab/Shift+Tab between the first and last real controls instead.
// Returns a cleanup function that removes the keydown listener.
export function trapModalFocus(modalElement) {
  if (!modalElement) return () => {};

  modalElement
    .querySelectorAll(':scope > div[aria-hidden="true"][tabindex="0"]')
    .forEach((sentinel) => sentinel.setAttribute('tabindex', '-1'));

  function handleKeyDown(event) {
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const focusables = Array.from(modalElement.querySelectorAll(MODAL_FOCUSABLE_SELECTORS)).filter(
      (element) => element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length,
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const { activeElement } = document;
    // Focus may start on a non-tabbable element (the tabindex=-1 title); the browser would Tab out of the modal, so pick the next/previous control ourselves.
    if (!focusables.includes(activeElement)) {
      event.preventDefault();
      const follows = (element) =>
        // eslint-disable-next-line no-bitwise
        activeElement.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING;
      if (event.shiftKey) {
        const previous = focusables.filter((element) => !follows(element));
        (previous[previous.length - 1] || last).focus();
      } else {
        (focusables.find(follows) || first).focus();
      }
      return;
    }
    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  modalElement.addEventListener('keydown', handleKeyDown);
  return () => modalElement.removeEventListener('keydown', handleKeyDown);
}

function getFormControl(fieldName) {
  const elementById = document.getElementById(fieldName);
  if (elementById) return elementById;

  return Array.from(document.querySelectorAll('input, textarea, select, [role="combobox"], [role="checkbox"], [role="radio"]')).find(
    (element) => element.getAttribute('name') === fieldName || element.getAttribute('id') === fieldName,
  );
}

export function getOrderedFormErrors(errorObject) {
  const fields = Object.keys(errorObject || {}).map((fieldName, index) => ({
    element: getFormControl(fieldName),
    errors: (errorObject[fieldName] && errorObject[fieldName].errors) || [],
    fieldName,
    index,
  }));

  fields.sort((first, second) => {
    if (!first.element || !second.element) return first.index - second.index;
    const position = first.element.compareDocumentPosition(second.element);
    if (position === Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position === Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return first.index - second.index;
  });

  return fields.reduce((orderedErrors, field) => {
    field.errors.forEach((error) => {
      const message = error && (error.message || error);
      if (message) orderedErrors.push({ element: field.element, fieldName: field.fieldName, message: String(message) });
    });
    return orderedErrors;
  }, []);
}

export function announceFormValidationErrors(errorObject, id) {
  const errors = getOrderedFormErrors(errorObject);
  if (!errors.length) return '';

  const messages = errors.map(({ message }) => message.trim().replace(/[.\s]+$/, ''));
  const summary = `${messages.length} error${messages.length === 1 ? '' : 's'}: ${messages.join('. ')}.`;
  const announcement = `${messages.length} error${messages.length === 1 ? '' : 's'} in the form`;
  announceToScreenReader(announcement, id);

  const firstInvalidControl = errors[0].element || getFormControl(errors[0].fieldName);
  if (firstInvalidControl && typeof firstInvalidControl.focus === 'function') firstInvalidControl.focus();

  return summary;
}
